import type {
  ArenaCapsule,
  Comet,
  Gate,
  HoleLayout,
  Planet,
  Target,
  Vec2,
  Wormhole,
} from "./physics.js";
import { mulberry32, randRange, seedFromString, splitSeed } from "./rng.js";

export const HOLES_PER_COURSE = 9;

export interface DailyCourse {
  date: string; // ISO date in UTC 13:37 rollover space, e.g. 2026-04-22
  seed: number;
  holes: HoleLayout[];
}

export function courseDateAt(nowMs: number): string {
  // The course day rolls over at 13:37 UTC.
  const shifted = new Date(nowMs - 13 * 3600_000 - 37 * 60_000);
  return shifted.toISOString().slice(0, 10);
}

export function buildDailyCourse(date: string): DailyCourse {
  // v9 (Day 11): daily H1 is a hand-authored intro (one planet,
  // guaranteed NOT on the tee→target line); daily H2 is a hand-authored
  // blocker (two planets, one guaranteed on the corridor). H3-H9 use
  // the continuous escalation generator as before. Added a new
  // wavy-river arena variant (multi-capsule sinusoidal channel) that
  // competes with the S-chain / big-ellipse at e≥0.55. Corridor-blocker
  // bias also re-introduced for plain/big ellipse holes at H3+ — the
  // planet placement seeds the first (non-capsule) planet near the
  // tee→target midpoint so late-hole variance can't roll a straight
  // shot. Layout + RNG-sequence changes invalidate v8 ghost trails
  // (cosmetic only — totals remain valid).
  const parent = seedFromString(`cosmic-par/v11/${date}`);
  const holeSeeds = splitSeed(parent, HOLES_PER_COURSE);
  const holes = holeSeeds.map((s: number, i: number) => {
    if (i === 0) return buildIntroHole(i, s);
    if (i === 1) return buildBlockerHole(i, s);
    return buildHole(i, s, { escalation: i / (HOLES_PER_COURSE - 1) });
  });
  return { date, seed: parent, holes };
}

// H1-class intro hole. Small round arena, generous target, exactly
// one modest gravity well placed OFF the tee→target line so the
// player learns the drag-back mechanic without being punished for
// shooting straight. Used by daily H1 and endless H1. Deterministic.
export function buildIntroHole(index: number, seed: number): HoleLayout {
  const rng = mulberry32(seed);
  const arenaRadiusX = 22;
  const arenaRadiusZ = 18;
  const tee: Vec2 = {
    x: -arenaRadiusX + 5,
    z: randRange(rng, -3, 3),
  };
  const target: Target = {
    cx: arenaRadiusX - 5,
    cz: randRange(rng, -3, 3),
    radius: 2.4,
  };
  // Place the single planet at the midpoint, offset perpendicular to
  // the tee→target axis by at least 7 units so a straight shot sails
  // clear. 50/50 above/below.
  const mid = {
    x: (tee.x + target.cx) / 2,
    z: (tee.z + target.cz) / 2,
  };
  const sideSign = rng() < 0.5 ? -1 : 1;
  const planet: Planet = {
    cx: mid.x + randRange(rng, -3, 3),
    cz: mid.z + sideSign * randRange(rng, 7, 10),
    radius: 1.8,
    strength: 60,
  };
  return {
    index,
    seed,
    bounds: {
      minX: -arenaRadiusX,
      maxX: arenaRadiusX,
      minZ: -arenaRadiusZ,
      maxZ: arenaRadiusZ,
    },
    arenaRadius: arenaRadiusX,
    arenaRadiusX,
    arenaRadiusZ,
    tee,
    target,
    planets: [planet],
    par: 2,
  };
}

// H2-class blocker hole. Two planets: one guaranteed on the
// tee→target corridor (the blocker) so a straight shot slingshots
// off it, one on the side for visual interest and secondary pull.
// Used by daily H2 and endless H2.
export function buildBlockerHole(index: number, seed: number): HoleLayout {
  const rng = mulberry32(seed);
  const arenaRadiusX = 26;
  const arenaRadiusZ = 20;
  const tee: Vec2 = {
    x: -arenaRadiusX + 5,
    z: randRange(rng, -3, 3),
  };
  const target: Target = {
    cx: arenaRadiusX - 5,
    cz: randRange(rng, -3, 3),
    radius: 2.2,
  };
  // Blocker on the corridor, 45-60% of the way tee→target. Slight
  // perpendicular jitter so it's not always dead-center.
  const t1 = randRange(rng, 0.45, 0.6);
  const corridorDX = target.cx - tee.x;
  const corridorDZ = target.cz - tee.z;
  const corridorLen = Math.hypot(corridorDX, corridorDZ) || 1;
  const perpX = -corridorDZ / corridorLen;
  const perpZ = corridorDX / corridorLen;
  const jitter1 = randRange(rng, -1.5, 1.5);
  const blocker: Planet = {
    cx: tee.x + corridorDX * t1 + perpX * jitter1,
    cz: tee.z + corridorDZ * t1 + perpZ * jitter1,
    radius: 2.2,
    strength: 75,
  };
  // Side planet well off the corridor, 30-70% along it.
  const sideSign = rng() < 0.5 ? -1 : 1;
  const t2 = randRange(rng, 0.3, 0.7);
  const sideOffset = sideSign * randRange(rng, 8, 11);
  const sidePlanet: Planet = {
    cx: tee.x + corridorDX * t2 + perpX * sideOffset,
    cz: tee.z + corridorDZ * t2 + perpZ * sideOffset,
    radius: 1.6,
    strength: 55,
  };
  return {
    index,
    seed,
    bounds: {
      minX: -arenaRadiusX,
      maxX: arenaRadiusX,
      minZ: -arenaRadiusZ,
      maxZ: arenaRadiusZ,
    },
    arenaRadius: arenaRadiusX,
    arenaRadiusX,
    arenaRadiusZ,
    tee,
    target,
    planets: [blocker, sidePlanet],
    par: 2,
  };
}

interface HoleOptions {
  /** 0..1 difficulty knob controlling distance, planet count / mass,
   *  target tightness. 0 = forgiving slam, 1 = threading-the-needle. */
  escalation: number;
}

// Legacy signature preserved for callers that don't need the knob.
// Clamps escalation to [0,1]; use buildHoleAtEscalation for endless mode.
export function buildHole(
  index: number,
  seed: number,
  options?: HoleOptions,
): HoleLayout {
  const e = clamp01(options?.escalation ?? index / (HOLES_PER_COURSE - 1));
  return buildHoleAtEscalation(index, seed, e);
}

// Endless-mode generator. Allows escalation > 1 so practice / free-play
// can keep ramping past H9 difficulty. Playability is preserved by
// capping a few specific knobs (target radius floor, wind magnitude)
// so the hole stays solvable even when e is large.
export function buildHoleAtEscalation(
  index: number,
  seed: number,
  escalation: number,
): HoleLayout {
  const rng = mulberry32(seed);
  const e = escalation < 0 ? 0 : escalation;

  // Arena variant — chosen from a single deterministic roll so the
  // RNG sequence stays stable regardless of which branch wins. Four
  // possibilities: plain ellipse, wavy river (multi-capsule sinusoidal
  // channel, v9), capsule-chain S-bend (v7), big dense ellipse (v8).
  // Guaranteed-special thresholds (e≥0.85 → river or chain) preserve
  // the Day 10 feel where late holes always do SOMETHING unusual.
  const variantRoll = rng();
  let variant: "plain" | "river" | "chain" | "bigEllipse" = "plain";
  if (e >= 0.85) {
    // Late-hole guarantee: ~half river, ~half S-chain.
    variant = variantRoll < 0.5 ? "river" : "chain";
  } else if (e >= 0.6) {
    // 40% river, 40% chain, 20% plain — mid-late holes feel distinctly
    // *shaped* most of the time.
    if (variantRoll < 0.4) variant = "river";
    else if (variantRoll < 0.8) variant = "chain";
  } else if (e >= 0.55) {
    // 25% river, else plain (or big-ellipse at e≥0.5 below).
    if (variantRoll < 0.25) variant = "river";
    else if (variantRoll < 0.4) variant = "bigEllipse";
  } else if (e >= 0.5) {
    // 15% big ellipse, else plain. No chain/river yet.
    if (variantRoll < 0.15) variant = "bigEllipse";
  }

  const wantsRiver = variant === "river";
  const wantsCapsule = variant === "chain";
  const bigEllipse = variant === "bigEllipse";

  // Arena shape + size escalates aggressively across the 9 holes so
  // H1 feels like a compact putt-green and H9 sprawls out into a
  // proper obstacle course. At e>1 it keeps stretching; the capsule-
  // chain arena (when triggered) overrides this entirely. The
  // bigEllipse branch fixes the base radius in the 60-70 range.
  const baseR = bigEllipse ? randRange(rng, 60, 70) : 22 + e * 30;
  const stretchX = bigEllipse ? 1.05 : 1 + e * 0.55;
  const stretchZ = bigEllipse ? 0.95 : Math.max(0.5, 1 - e * 0.25);
  const arenaRadius = baseR;
  const arenaRadiusX = baseR * stretchX;
  const arenaRadiusZ = baseR * stretchZ;
  const arenaHalfX = arenaRadiusX;
  const arenaHalfZ = arenaRadiusZ;

  // Tee on the left edge of the ellipse, target on the right, with
  // some z-variance that grows with escalation so late holes aren't
  // straight-line shots.
  const teeNoise = 0.3 + Math.min(e, 1) * 0.5;
  const teeX = -arenaRadiusX + randRange(rng, 4, 7);
  const teeZ = randRange(rng, -arenaRadiusZ * teeNoise * 0.55, arenaRadiusZ * teeNoise * 0.55);
  const tee: Vec2 = clampToEllipse(teeX, teeZ, arenaRadiusX - 2, arenaRadiusZ - 2);

  const targetX = arenaRadiusX - randRange(rng, 4, 7);
  const targetZ = randRange(rng, -arenaRadiusZ * teeNoise * 0.55, arenaRadiusZ * teeNoise * 0.55);
  const targetCenter = clampToEllipse(
    targetX,
    targetZ,
    arenaRadiusX - 2,
    arenaRadiusZ - 2,
  );
  const target: Target = {
    cx: targetCenter.x,
    cz: targetCenter.z,
    // Target radius shrinks with escalation but clamps at 0.9 so
    // endless-mode holes stay capturable.
    radius: Math.max(
      0.9,
      randRange(rng, 1.9 - e * 0.5, 2.3 - e * 0.5),
    ),
  };
  // Pulsing target (v9) — at e ≥ 0.85 (≈ H9+) the target radius
  // oscillates around its base so timing the approach matters.
  // Amplitude capped at 55% of base so effective radius stays
  // positive. Applied to any arena shape — orthogonal to chain/river
  // since it's a target property, not a field effect.
  if (e >= 0.85 && rng() < 0.5) {
    const amp = target.radius * 0.55;
    target.pulse = {
      amplitude: amp,
      omega: randRange(rng, 1.2, 2.0),
      phase: rng() * Math.PI * 2,
    };
  }

  let arenaSegments: ArenaCapsule[] | undefined;
  let capsuleBounds:
    | { minX: number; maxX: number; minZ: number; maxZ: number }
    | undefined;
  if (wantsRiver) {
    const river = buildWavyRiverArena(rng, e, tee, { x: target.cx, z: target.cz });
    if (river) {
      arenaSegments = river.segments;
      capsuleBounds = river.bounds;
    }
  } else if (wantsCapsule) {
    const chain = buildCapsuleChainArena(rng, e, tee, { x: target.cx, z: target.cz });
    if (chain) {
      arenaSegments = chain.segments;
      capsuleBounds = chain.bounds;
    }
  }

  // Planet count ramps hard and keeps climbing in endless mode; v6
  // floor 1+floor(e*5) guarantees late holes don't roll a scatter.
  // v8: bigEllipse holes get +2 planets so the extra space stays dense.
  const planetCount =
    Math.max(1 + Math.floor(e * 5), Math.round(0.6 + e * 6.4)) +
    (bigEllipse ? 2 : 0);
  // Boss planet — escalation ≥ 0.5, 55% chance. `let` because the
  // v6 feature floor below may force-promote planet[0] into a boss
  // when both the boss roll and wormhole roll whiff. v8: bigEllipse
  // always gets a boss (still consumes the rng() so sequence is stable
  // across the branch — flag forces true regardless of the roll).
  let bossPlanet = (e >= 0.5 && rng() < 0.55) || bigEllipse;
  const planets: Planet[] = [];
  let attempts = 0;
  // Corridor-blocker bias (v9 reintroduction): for plain/big-ellipse
  // holes, place the first planet on the tee→target line so escalation
  // variance can't roll a straight shot. Capsule and river arenas
  // already force navigation via shape. Only applies on the initial
  // attempt of the first planet; any rejection falls through to the
  // regular random sample next attempt so we never loop forever.
  let biasFirstPlanet = !arenaSegments;
  // On capsule/river holes the channel width is the hard upper bound
  // on planet size — a planet wider than ~40% of the channel radius
  // leaves too little room for the ball to thread through. Pre-compute
  // the smallest segment radius so we can cap each planet below.
  const minChannelR =
    arenaSegments && arenaSegments.length > 0
      ? Math.min(...arenaSegments.map((s) => s.radius))
      : Infinity;
  const maxPlanetR = Math.max(1.0, minChannelR * 0.4 - 0.5);
  while (planets.length < planetCount && attempts < 400) {
    attempts++;
    // Bigger, heavier planets at high escalation — they actually bend
    // trajectories instead of just sitting there. v6 also raises the
    // *minimum* radius with escalation so late holes can't roll all
    // 1.2-radius pebbles the ball sails right past.
    const isBoss = bossPlanet && planets.length === 0;
    const isBlocker = biasFirstPlanet && planets.length === 0;
    let radius = isBoss
      ? randRange(rng, 3.4, 4.6)
      : isBlocker
        ? randRange(rng, 2.0 + Math.min(e, 1) * 0.8, 2.8 + e * 1.2)
        : randRange(rng, 1.2 + Math.min(e, 1) * 0.8, 2.4 + e * 1.2);
    // Clamp to channel width on capsule/river holes so the ball has
    // a passage even on wide-radius endless planets (which otherwise
    // roll up to ~16u and block a ~16u channel completely).
    if (arenaSegments) {
      radius = Math.min(radius, maxPlanetR);
    }
    const sizeFactor = radius / 1.8;
    const strength =
      sizeFactor * randRange(rng, 50 + e * 30, 100 + e * 110);
    let cx: number;
    let cz: number;
    if (arenaSegments) {
      // Sample inside the arena's rect bounds; accept only if inside
      // some capsule with enough margin to stay clear of its wall.
      const bx = capsuleBounds!;
      cx = randRange(rng, bx.minX + radius + 1, bx.maxX - radius - 1);
      cz = randRange(rng, bx.minZ + radius + 1, bx.maxZ - radius - 1);
      let minD = Infinity;
      let rAtMin = 0;
      for (const s of arenaSegments) {
        const cp = closestPointOnSegment2(cx, cz, s.ax, s.az, s.bx, s.bz);
        const d = Math.hypot(cx - cp.x, cz - cp.z);
        if (d < minD) {
          minD = d;
          rAtMin = s.radius;
        }
      }
      if (minD > rAtMin - radius - 1) continue;
    } else if (isBlocker) {
      // Seed position on the corridor 35-65% along tee→target with
      // small perpendicular jitter. Skip the bias flag regardless so
      // a rejected placement falls back to random next attempt.
      biasFirstPlanet = false;
      const t = randRange(rng, 0.35, 0.65);
      const cdx = target.cx - tee.x;
      const cdz = target.cz - tee.z;
      const clen = Math.hypot(cdx, cdz) || 1;
      const perpJ = randRange(rng, -2, 2);
      cx = tee.x + cdx * t + (-cdz / clen) * perpJ;
      cz = tee.z + cdz * t + (cdx / clen) * perpJ;
      const ax = arenaRadiusX - radius - 2;
      const az = arenaRadiusZ - radius - 2;
      if ((cx * cx) / (ax * ax) + (cz * cz) / (az * az) > 1) continue;
    } else {
      cx = randRange(rng, -arenaHalfX + radius + 2, arenaHalfX - radius - 2);
      cz = randRange(rng, -arenaHalfZ + radius + 2, arenaHalfZ - radius - 2);
      const ax = arenaRadiusX - radius - 2;
      const az = arenaRadiusZ - radius - 2;
      if ((cx * cx) / (ax * ax) + (cz * cz) / (az * az) > 1) continue;
    }
    const p: Planet = { cx, cz, radius, strength };
    const teeClear = 6 - Math.min(e, 1) * 2.5;
    const targetClear = 3 - Math.min(e, 1) * 1.0;
    if (
      dist(p.cx, p.cz, tee.x, tee.z) < radius + teeClear ||
      dist(p.cx, p.cz, target.cx, target.cz) <
        radius + target.radius + targetClear
    ) {
      continue;
    }
    let overlaps = false;
    for (const q of planets) {
      if (dist(p.cx, p.cz, q.cx, q.cz) < p.radius + q.radius + 2) {
        overlaps = true;
        break;
      }
    }
    if (overlaps) continue;
    planets.push(p);
  }

  // Repulsor planet (v6) — at e ≥ 0.5, 30% chance to flip one
  // non-boss planet's strength sign so it pushes the ball away
  // instead of pulling. The inverse-square term runs the same way;
  // `strength < 0` is the only difference. Skipped on capsule-chain
  // holes (mixing systems is v8's problem).
  if (!arenaSegments && e >= 0.5 && planets.length >= 2 && rng() < 0.3) {
    const flipIdx = 1 + Math.floor(rng() * (planets.length - 1));
    const picked = planets[flipIdx]!;
    planets[flipIdx] = { ...picked, strength: -picked.strength };
  }

  // Moving planet (v9) — at e ≥ 0.6, 25% chance to add an orbit
  // descriptor to one non-boss, non-repulsor planet. The planet traces
  // a small circle around its original center (radius 4-8u, omega
  // 0.5-1.1 rad/s, random phase). Skipped on capsule-chain/river holes
  // to keep those puzzles static. Determinism preserved — position is
  // a pure function of sim time inside physics.
  if (!arenaSegments && e >= 0.6 && planets.length >= 2 && rng() < 0.25) {
    const mvIdx = 1 + Math.floor(rng() * (planets.length - 1));
    const picked = planets[mvIdx]!;
    if (picked.strength > 0) {
      planets[mvIdx] = {
        ...picked,
        orbit: {
          radius: randRange(rng, 4, 8),
          omega: randRange(rng, 0.5, 1.1),
          phase: rng() * Math.PI * 2,
        },
      };
    }
  }

  // (Corridor-block was removed in Day 10 — the force-relocate logic
  // produced collider/visual mismatches and the pulsing gate +
  // capsule-chain arenas are stronger "force navigation" mechanics
  // anyway. Late holes get their teeth from those + the planet-count
  // floor + boss + wormhole + repulsor + wind.)

  // Wormhole pairs. On plain/big-ellipse arenas we keep the classic
  // "top-half vs bottom-half" layout. On capsule/river arenas, we
  // now place pairs INSIDE the chain (far apart along the channel)
  // so they're always within the playable area. Pair count scales
  // with escalation: H7-ish gets 1 pair, late endless (e≥3) can get
  // up to 4. Each additional pair needs a clear placement spot or
  // it's skipped.
  let wormholes: Wormhole[] | undefined;
  if (arenaSegments) {
    wormholes = maybeSpawnChainWormholes(
      rng,
      e,
      arenaSegments,
      tee,
      target,
      planets,
    );
  } else {
    wormholes = maybeSpawnWormholePair(
      rng,
      e,
      arenaHalfX,
      arenaHalfZ,
      tee,
      target,
      planets,
    );
    // Extra pairs on big-ellipse or wide plain arenas at high e.
    const extra = Math.max(0, Math.floor((e - 1) / 1.5));
    for (let k = 0; k < extra; k++) {
      const more = forceSpawnWormholePair(
        rng,
        e,
        arenaHalfX,
        arenaHalfZ,
        tee,
        target,
        planets,
      );
      if (more) {
        const base = wormholes ?? [];
        // Append with partner indices offset so they reference each other.
        const offset = base.length;
        wormholes = [
          ...base,
          { ...more[0]!, partnerIndex: offset + 1 },
          { ...more[1]!, partnerIndex: offset },
        ];
      }
    }
  }

  // Pulsing gate (v7): 50% at e≥0.5, guaranteed at e≥0.7. Placed
  // perpendicular to the tee→target line at 50-70% along it.
  // v8: bigEllipse branch forces a gate regardless of probability.
  const gates = maybeSpawnGate(
    rng,
    e,
    tee,
    target,
    arenaRadiusX,
    arenaRadiusZ,
    arenaSegments,
    bigEllipse,
  );

  // Feature floor (v6): at e ≥ 0.5 we want *at least one* of
  // {boss planet, wormhole pair, gate} so H5+ never rolls as a
  // lifeless scatter. Capsule-chain holes are exempt (the chain
  // itself is the feature). If everything whiffed, force-spawn a
  // wormhole; if placement fails, promote planet[0] to boss.
  if (!arenaSegments && e >= 0.5 && !bossPlanet && !wormholes && !gates?.length) {
    wormholes = forceSpawnWormholePair(
      rng,
      e,
      arenaHalfX,
      arenaHalfZ,
      tee,
      target,
      planets,
    );
    if (!wormholes && planets.length > 0) {
      const first = planets[0]!;
      planets[0] = {
        ...first,
        radius: Math.max(first.radius, 3.4),
        strength: Math.abs(first.strength) *
          (first.radius < 3.4 ? (3.4 / Math.max(first.radius, 1)) : 1.0),
      };
      bossPlanet = true;
    }
  }

  // Solar wind disabled (2026-04-26) — the constant drift acceleration
  // had no clear visual cue (the streamer particles were too subtle on
  // mobile + busy backdrops) and made shots take 10+ seconds to settle
  // on H7+ holes. We keep the 2 RNG rolls that the wind block used to
  // consume so the v11 daily seed still produces byte-identical layouts
  // for everything downstream (comets, gates, par, …) — only `windAccel`
  // is left undefined now.
  const windAccel: Vec2 | undefined = undefined;
  if (!arenaSegments && e >= 0.7) {
    rng(); // was: theta
    rng(); // was: randRange for magnitude (lerp consumes 1 roll)
  }

  // Comet hazard (v11) — at e ≥ 0.5, 60% chance to spawn one sweeping
  // hazard that resets the ball on contact. Guaranteed at e ≥ 0.85.
  // Allowed on plain / bigEllipse / chain (S-bend) arenas; skipped on
  // river variants because the narrow winding channel would make a
  // sweeping hazard feel unfair. Track runs roughly perpendicular to
  // tee→target across the middle third of the arena so it threatens
  // the direct corridor. Lowered threshold from the v11 release so
  // playtesters actually encounter them on H5+ in endless rather
  // than H8+.
  let comets: Comet[] | undefined;
  if (!wantsRiver && e >= 0.5 && (e >= 0.85 || rng() < 0.6)) {
    const cdx = target.cx - tee.x;
    const cdz = target.cz - tee.z;
    const clen = Math.hypot(cdx, cdz) || 1;
    // Perpendicular unit vector to the corridor.
    const perpX = -cdz / clen;
    const perpZ = cdx / clen;
    // Anchor near the midpoint with modest jitter so the comet
    // actually crosses the tee→target line.
    const tFrac = randRange(rng, 0.4, 0.6);
    const mx = tee.x + cdx * tFrac;
    const mz = tee.z + cdz * tFrac;
    const halfLen = randRange(rng, 14, 22);
    const ax = clampAbs(mx + perpX * halfLen, arenaRadiusX - 2);
    const az = clampAbs(mz + perpZ * halfLen, arenaRadiusZ - 2);
    const bx = clampAbs(mx - perpX * halfLen, arenaRadiusX - 2);
    const bz = clampAbs(mz - perpZ * halfLen, arenaRadiusZ - 2);
    comets = [
      {
        ax,
        az,
        bx,
        bz,
        period: randRange(rng, 4.5, 6.5),
        phase: rng(),
        radius: randRange(rng, 1.1, 1.5),
      },
    ];
  }

  // Par keyed off the visible hole index so the daily card reads
  // naturally (par 2 early, par 4 late).
  const par = index < 3 ? 2 : index < 6 ? 3 : 4;

  const bounds = capsuleBounds ?? {
    minX: -arenaHalfX,
    maxX: arenaHalfX,
    minZ: -arenaHalfZ,
    maxZ: arenaHalfZ,
  };

  // powerScale (v10) — scales MAX_POWER so big endless arenas can
  // actually be traversed in a reasonable number of strokes. Daily
  // spans (≤ 160) keep scale 1 so replays are unchanged; endless
  // ramps up to 3x on the widest wavy-river holes. Deterministic:
  // a pure function of the bounds the caller already has.
  const span = Math.max(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ);
  const powerScale = Math.max(1, Math.min(3, span / 160));

  return {
    index,
    seed,
    bounds,
    arenaRadius,
    arenaRadiusX,
    arenaRadiusZ,
    tee,
    target,
    planets,
    wormholes,
    windAccel,
    gates,
    comets,
    arenaSegments,
    powerScale,
    par,
  };
}

// Clamp a coordinate to [-abs, abs] — keeps comet track endpoints
// inside the arena when the perpendicular anchor pushes them past the
// ellipse bounds.
function clampAbs(v: number, abs: number): number {
  if (v > abs) return abs;
  if (v < -abs) return -abs;
  return v;
}

// Pulls a point in along its current bearing if it sits outside the
// ellipse defined by (rx, rz). Used to keep tee / target inside the
// arena even when the rect-space sample overshoots one axis.
function clampToEllipse(x: number, z: number, rx: number, rz: number): Vec2 {
  const q = (x * x) / (rx * rx) + (z * z) / (rz * rz);
  if (q <= 1) return { x, z };
  const s = 1 / Math.sqrt(q);
  return { x: x * s, z: z * s };
}

function maybeSpawnWormholePair(
  rng: () => number,
  escalation: number,
  arenaHalfX: number,
  arenaHalfZ: number,
  tee: Vec2,
  target: Target,
  planets: Planet[],
): Wormhole[] | undefined {
  // Escalation gated so early holes stay vanilla. At e≥0.75 (≈ H7+)
  // a pair is guaranteed; at 0.5–0.75 it's a 65% chance; below 0.5
  // none. Late holes feel distinctly "endgame" with the teleport.
  if (escalation < 0.5) return undefined;
  if (escalation < 0.75 && rng() > 0.65) return undefined;
  return buildWormholePair(rng, escalation, arenaHalfX, arenaHalfZ, tee, target, planets);
}

// v6 feature-floor hatch: same placement logic as maybe… but no
// probability gate. Called only when we've already committed to
// forcing a pair because neither boss nor maybe-roll succeeded.
function forceSpawnWormholePair(
  rng: () => number,
  escalation: number,
  arenaHalfX: number,
  arenaHalfZ: number,
  tee: Vec2,
  target: Target,
  planets: Planet[],
): Wormhole[] | undefined {
  return buildWormholePair(rng, escalation, arenaHalfX, arenaHalfZ, tee, target, planets);
}

// Wormholes along a capsule-chain / wavy-river arena. Pairs are placed
// INSIDE capsule segments (sampled along each segment's centerline)
// so they're always legal to enter. For each pair, pick two capsules
// that are far apart along the chain (so teleporting covers meaningful
// ground) and drop a wormhole in each. Count scales with escalation:
// ~1 pair at e=0.7, up to 4 pairs at e≥3. Skipped below e=0.7.
function maybeSpawnChainWormholes(
  rng: () => number,
  escalation: number,
  segments: ArenaCapsule[],
  tee: Vec2,
  target: Target,
  planets: Planet[],
): Wormhole[] | undefined {
  if (escalation < 0.7) return undefined;
  if (segments.length < 3) return undefined;

  const radius = 1.2 + Math.min(escalation, 3) * 0.3;
  const clearance = radius + 1.8;
  const pairs: Wormhole[] = [];
  const maxPairs = Math.max(1, Math.min(4, Math.floor(escalation / 1.2)));

  // Track where we've already placed things so pairs don't stack.
  const placedSpots: { x: number; z: number }[] = [];

  for (let p = 0; p < maxPairs; p++) {
    // For pair p, try different capsule combinations: take segments
    // "early" and "late" so the teleport is meaningful. Each pair
    // gets a different zone offset so they don't all clump.
    const zoneA = p / maxPairs; // 0, 1/n, 2/n, …
    const zoneB = 1 - zoneA - 0.05;
    const idxA = Math.floor(zoneA * (segments.length - 1));
    const idxB = Math.max(idxA + 1, Math.floor(zoneB * (segments.length - 1)));
    if (idxB >= segments.length || idxB <= idxA) continue;

    const a = placeInCapsule(
      rng,
      segments[idxA]!,
      tee,
      target,
      planets,
      placedSpots,
      radius,
      clearance,
    );
    if (!a) continue;
    const b = placeInCapsule(
      rng,
      segments[idxB]!,
      tee,
      target,
      planets,
      placedSpots,
      radius,
      clearance,
    );
    if (!b) continue;

    placedSpots.push(a, b);
    const baseIdx = pairs.length;
    pairs.push(
      { cx: a.x, cz: a.z, radius, partnerIndex: baseIdx + 1 },
      { cx: b.x, cz: b.z, radius, partnerIndex: baseIdx },
    );
  }

  return pairs.length > 0 ? pairs : undefined;
}

// Sample a legal spot inside a single capsule segment — along its
// midline with small perpendicular jitter, clearing tee/target/planets
// and any already-placed wormholes.
function placeInCapsule(
  rng: () => number,
  seg: ArenaCapsule,
  tee: Vec2,
  target: Target,
  planets: Planet[],
  avoidSpots: { x: number; z: number }[],
  radius: number,
  clearance: number,
): { x: number; z: number } | null {
  const dx = seg.bx - seg.ax;
  const dz = seg.bz - seg.az;
  const len = Math.hypot(dx, dz) || 1;
  const ux = dx / len;
  const uz = dz / len;
  const px = -uz;
  const pz = ux;
  const jitterMax = Math.max(0, seg.radius - clearance - 1);

  for (let i = 0; i < 24; i++) {
    const t = randRange(rng, 0.2, 0.8);
    const perp = randRange(rng, -jitterMax, jitterMax);
    const x = seg.ax + ux * t * len + px * perp;
    const z = seg.az + uz * t * len + pz * perp;
    if (dist(x, z, tee.x, tee.z) < clearance + 3) continue;
    if (dist(x, z, target.cx, target.cz) < clearance + target.radius) continue;
    let clash = false;
    for (const p of planets) {
      if (dist(x, z, p.cx, p.cz) < p.radius + clearance) { clash = true; break; }
    }
    if (clash) continue;
    for (const s of avoidSpots) {
      if (dist(x, z, s.x, s.z) < radius * 4) { clash = true; break; }
    }
    if (clash) continue;
    return { x, z };
  }
  return null;
}

function buildWormholePair(
  rng: () => number,
  escalation: number,
  arenaHalfX: number,
  arenaHalfZ: number,
  tee: Vec2,
  target: Target,
  planets: Planet[],
): Wormhole[] | undefined {
  const radius = 1.2 + escalation * 0.3;
  const clearance = radius + 1.8;

  // Place the first wormhole in the upper third of the arena, the
  // partner in the lower third — mirrored across z so hitting them is a
  // diagonal skill shot rather than a straight blast.
  const a = placeWormhole(
    rng,
    arenaHalfX,
    arenaHalfZ,
    tee,
    target,
    planets,
    radius,
    clearance,
    "top",
  );
  if (!a) return undefined;
  const b = placeWormhole(
    rng,
    arenaHalfX,
    arenaHalfZ,
    tee,
    target,
    planets,
    radius,
    clearance,
    "bottom",
    a,
  );
  if (!b) return undefined;

  return [
    { cx: a.x, cz: a.z, radius, partnerIndex: 1 },
    { cx: b.x, cz: b.z, radius, partnerIndex: 0 },
  ];
}

function placeWormhole(
  rng: () => number,
  arenaHalfX: number,
  arenaHalfZ: number,
  tee: Vec2,
  target: Target,
  planets: Planet[],
  radius: number,
  clearance: number,
  quadrant: "top" | "bottom",
  avoid?: Vec2,
): Vec2 | null {
  // Sample a few candidates in the target quadrant and pick the first
  // that clears the tee, target, planets, and the other wormhole.
  // IMPORTANT: x² and z² ranges are rectangular; the arena is an
  // ellipse. Reject anything outside (x/rX)² + (z/rZ)² ≤ 1 — margin
  // accounts for the wormhole radius so the event horizon sits inside
  // the playfield, not clipping the boundary.
  const insetRX = Math.max(1, arenaHalfX - radius - 0.5);
  const insetRZ = Math.max(1, arenaHalfZ - radius - 0.5);
  for (let i = 0; i < 40; i++) {
    const x = randRange(rng, -arenaHalfX * 0.85, arenaHalfX * 0.85);
    const zSpan =
      quadrant === "top"
        ? randRange(rng, arenaHalfZ * 0.35, arenaHalfZ * 0.85)
        : randRange(rng, -arenaHalfZ * 0.85, -arenaHalfZ * 0.35);
    const z = zSpan;
    // Ellipse inside test against the inset bounds (accounts for the
    // wormhole's own radius so the full circle fits).
    const q = (x * x) / (insetRX * insetRX) + (z * z) / (insetRZ * insetRZ);
    if (q > 1) continue;
    if (dist(x, z, tee.x, tee.z) < clearance + 3) continue;
    if (dist(x, z, target.cx, target.cz) < clearance + target.radius) continue;
    if (avoid && dist(x, z, avoid.x, avoid.z) < radius * 4) continue;
    let collides = false;
    for (const p of planets) {
      if (dist(x, z, p.cx, p.cz) < p.radius + clearance) {
        collides = true;
        break;
      }
    }
    if (!collides) return { x, z };
  }
  return null;
}

// (ensureCorridorBlock was removed in Day 10 — the force-relocate
// produced collider/visual mismatches. Pulsing gates + capsule chains
// are the live "force navigation" mechanics.)

function maybeSpawnGate(
  rng: () => number,
  escalation: number,
  tee: Vec2,
  target: Target,
  arenaRadiusX: number,
  arenaRadiusZ: number,
  arenaSegments?: ArenaCapsule[],
  force?: boolean,
): Gate[] | undefined {
  if (escalation < 0.5 && !force) return undefined;
  // Consume the [0.5,0.7) probability roll unconditionally so the RNG
  // sequence is stable whether or not `force` short-circuits the skip.
  const probSkip =
    escalation >= 0.5 && escalation < 0.7 ? rng() < 0.5 : false;
  if (!force && probSkip) return undefined;

  // On capsule holes, anchor the gate on the middle segment's midpoint
  // and align normal to that segment's axis so the gate acts as a real
  // chokepoint inside the bend. Works for both the 3-capsule S-chain
  // and the 5-7-capsule wavy river. Off-chain holes use the classic
  // straight-line placement.
  let cx: number, cz: number, nx: number, nz: number, tanX: number, tanZ: number;
  if (arenaSegments && arenaSegments.length >= 2) {
    const midIdx = Math.floor(arenaSegments.length / 2);
    const b = arenaSegments[midIdx]!;
    const t = randRange(rng, 0.4, 0.6);
    cx = b.ax + (b.bx - b.ax) * t;
    cz = b.az + (b.bz - b.az) * t;
    const bx2 = b.bx - b.ax;
    const bz2 = b.bz - b.az;
    const blen = Math.hypot(bx2, bz2) || 1;
    nx = bx2 / blen;
    nz = bz2 / blen;
    tanX = -nz;
    tanZ = nx;
  } else {
    const tx = target.cx - tee.x;
    const tz = target.cz - tee.z;
    const len = Math.hypot(tx, tz);
    if (len < 1e-6) return undefined;
    const ux = tx / len;
    const uz = tz / len;
    nx = ux;
    nz = uz;
    tanX = -uz;
    tanZ = ux;
    const t = randRange(rng, 0.5, 0.7);
    cx = tee.x + ux * len * t;
    cz = tee.z + uz * len * t;
  }

  // Estimate arena width along the tangent at this point. On capsule
  // chains, width comes from the nearest capsule's radius; even when
  // the chord midpoint sits outside the capsules (typical for S-bends)
  // fall back to the smallest capsule radius so the gate still exists.
  let widthToWall: number;
  if (arenaSegments && arenaSegments.length > 0) {
    let best = 0;
    let minR = Infinity;
    for (const s of arenaSegments) {
      if (s.radius < minR) minR = s.radius;
      const cp = closestPointOnSegment2(cx, cz, s.ax, s.az, s.bx, s.bz);
      const d = Math.hypot(cx - cp.x, cz - cp.z);
      const slack = s.radius - d;
      if (slack > best) best = slack;
    }
    widthToWall = best > 3 ? best : minR;
  } else {
    widthToWall = rayEllipseHalfLength(cx, cz, tanX, tanZ, arenaRadiusX, arenaRadiusZ);
  }
  if (widthToWall < 3) return undefined;
  const halfWidth = widthToWall * randRange(rng, 0.4, 0.6);

  const closedSeconds = 1.4 + escalation * 0.6;
  const openSeconds = Math.max(0.5, 1.0 - escalation * 0.4);

  return [
    {
      cx,
      cz,
      nx,
      nz,
      halfWidth,
      thickness: 0.6,
      closedSeconds,
      openSeconds,
      phase: rng(),
    },
  ];
}

// Distance from point (cx,cz) along unit dir (dx,dz) to the ellipse
// (x/a)² + (z/b)² = 1. Returns the positive half-length; 0 if the
// ray sits outside.
function rayEllipseHalfLength(
  cx: number,
  cz: number,
  dx: number,
  dz: number,
  a: number,
  b: number,
): number {
  // (cx + t*dx)²/a² + (cz + t*dz)²/b² = 1
  const A = (dx * dx) / (a * a) + (dz * dz) / (b * b);
  const B = 2 * ((cx * dx) / (a * a) + (cz * dz) / (b * b));
  const C = (cx * cx) / (a * a) + (cz * cz) / (b * b) - 1;
  const disc = B * B - 4 * A * C;
  if (disc < 0 || A < 1e-9) return 0;
  const s = Math.sqrt(disc);
  const tPos = (-B + s) / (2 * A);
  const tNeg = (-B - s) / (2 * A);
  return Math.max(0, Math.min(Math.abs(tPos), Math.abs(tNeg)));
}

// Build a wavy-river arena. Wave parameters scale with the actual
// tee→target distance so long endless arenas stay visibly wavy (the
// pre-v11 version capped amplitude at 22u while channels could stretch
// 5000u+, which read as a straight line). At escalation ≥ 1.0 the
// river forks into a second interlocking channel (opposite phase) that
// crosses the primary every half-cycle — gives a "constellation"
// feel with multiple paths to the target. Segment budget is bounded
// by the shader's MAX_CAPSULES_IN_SHADER (24).
const SHADER_CAPSULE_BUDGET = 24;

// Exported so the attract-mode demo (src/game/demo.ts) can build
// obstacle-free wavy-river layouts behind the welcome menu.
export function buildWavyRiverArena(
  rng: () => number,
  escalation: number,
  tee: Vec2,
  target: Vec2,
): {
  segments: ArenaCapsule[];
  bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
} | null {
  for (let attempt = 0; attempt < 5; attempt++) {
    const eClamped = Math.min(escalation, 2.5);

    // Forward axis = tee→target direction; perp = left-hand rotation.
    const dx0 = target.x - tee.x;
    const dz0 = target.z - tee.z;
    const forwardLen = Math.hypot(dx0, dz0) || 1;
    const ux = dx0 / forwardLen;
    const uz = dz0 / forwardLen;
    const px = -uz;
    const pz = ux;

    // Channel extends slightly behind tee and past target so the end
    // capsules have slack for tee/target placement.
    const behindTee = 6;
    const pastTarget = 6;
    const channelLen = forwardLen + behindTee + pastTarget;

    // At e ≥ 1.0 we build TWO interlocking sine channels that cross
    // every half-cycle → constellation / braided-river feel.
    const isMultiPath = escalation >= 1.0;

    // Amplitude scales with channel length so long arenas visibly
    // wave instead of reading as a line. Cap prevents runaway bends
    // that would push the arena way beyond its bounds rectangle.
    const amp = Math.min(Math.max(10, channelLen * 0.1), 180);

    // Cycles — one full sine wave per ~380 world units, so both tight
    // daily arenas and long endless arenas stay legible.
    const cycles = Math.max(1.25, Math.min(8, channelLen / 380));

    // Segments per path: 3 per cycle is the minimum for a smooth
    // curve. Budget is 24 capsules total (shader-bound); split equally
    // between paths in multi-path mode.
    const maxPerPath = isMultiPath
      ? Math.floor(SHADER_CAPSULE_BUDGET / 2)
      : SHADER_CAPSULE_BUDGET - 2;
    const segPerPath = Math.max(
      6,
      Math.min(maxPerPath, Math.ceil(cycles * 3)),
    );

    const basePhase = rng() * Math.PI * 2;
    const bendSign = rng() < 0.5 ? 1 : -1;
    const rBase = 8.5 + eClamped * 3;

    const segments: ArenaCapsule[] = [];

    const buildPath = (phaseOffset: number): void => {
      const pathPoints: Vec2[] = [];
      for (let i = 0; i <= segPerPath; i++) {
        const t = i / segPerPath;
        const forward = -behindTee + t * channelLen;
        const perpOffset =
          bendSign *
          amp *
          Math.sin(t * cycles * Math.PI * 2 + basePhase + phaseOffset);
        pathPoints.push({
          x: tee.x + ux * forward + px * perpOffset,
          z: tee.z + uz * forward + pz * perpOffset,
        });
      }
      for (let i = 0; i < segPerPath; i++) {
        const a = pathPoints[i]!;
        const b = pathPoints[i + 1]!;
        const isEnd = i === 0 || i === segPerPath - 1;
        const widen = isEnd ? 2.5 : 0;
        const radius = rBase + widen + randRange(rng, -0.6, 0.6);
        segments.push({ ax: a.x, az: a.z, bx: b.x, bz: b.z, radius });
      }
    };

    // Primary path always spans tee → target at phase 0 so tee/target
    // anchors live in its end capsules.
    buildPath(0);
    // Braid: opposite-phase parallel channel crossing every half-cycle.
    if (isMultiPath) buildPath(Math.PI);

    // Verify tee sits inside the primary path's first segment and
    // target inside its last segment — critical so shot physics has
    // a legal start + end position.
    const first = segments[0]!;
    const lastPrimary = segments[segPerPath - 1]!;
    const teeIn = distToSegment(tee.x, tee.z, first) <= first.radius - 1;
    const targetIn =
      distToSegment(target.x, target.z, lastPrimary) <=
      lastPrimary.radius - 1;
    if (!teeIn || !targetIn) continue;

    // Bounds circumscribe every capsule with its radius.
    let minX = Infinity,
      maxX = -Infinity,
      minZ = Infinity,
      maxZ = -Infinity;
    for (const s of segments) {
      minX = Math.min(minX, s.ax - s.radius, s.bx - s.radius);
      maxX = Math.max(maxX, s.ax + s.radius, s.bx + s.radius);
      minZ = Math.min(minZ, s.az - s.radius, s.bz - s.radius);
      maxZ = Math.max(maxZ, s.az + s.radius, s.bz + s.radius);
    }
    return { segments, bounds: { minX, maxX, minZ, maxZ } };
  }
  return null;
}

// Build a 3-capsule S-chain from tee → target. Re-rolls up to 5 times
// until tee sits inside capsule A and target inside capsule C.
// Exported so the attract-mode demo (src/game/demo.ts) can build
// obstacle-free capsule-chain layouts behind the welcome menu.
export function buildCapsuleChainArena(
  rng: () => number,
  escalation: number,
  tee: Vec2,
  target: Vec2,
): {
  segments: ArenaCapsule[];
  bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
} | null {
  for (let attempt = 0; attempt < 5; attempt++) {
    // v8: bump length (25+e*30) and radius (~12+e*6) so the chain
    // feels sprawling at high e. rB scaled proportionally (~11+e*5).
    const eClamped = Math.min(escalation, 2.5);
    const lenA = 25 + eClamped * 30;
    const rA = randRange(rng, 12 + eClamped * 4, 14 + eClamped * 8);
    const lenB = 20 + eClamped * 22;
    const rB = randRange(rng, 10 + eClamped * 3, 12 + eClamped * 7);
    const lenC = 25 + eClamped * 30;
    const rC = randRange(rng, 12 + eClamped * 4, 14 + eClamped * 8);

    // Direction tee→target, unit vector.
    const dx0 = target.x - tee.x;
    const dz0 = target.z - tee.z;
    const d0 = Math.hypot(dx0, dz0) || 1;
    const ux = dx0 / d0;
    const uz = dz0 / d0;

    // Capsule A: start just behind tee, head toward target.
    const aAx = tee.x - ux * 3;
    const aAz = tee.z - uz * 3;
    const aBx = aAx + ux * lenA;
    const aBz = aAz + uz * lenA;

    // Capsule B: bends off A's far end at ~70-90° from A's axis.
    const bendAngleDeg = randRange(rng, 70, 90);
    const bendSign = rng() < 0.5 ? 1 : -1;
    const rad = (bendAngleDeg * Math.PI) / 180;
    // Rotate (ux,uz) by ±rad to get B's axis direction.
    const cosR = Math.cos(rad);
    const sinR = Math.sin(rad) * bendSign;
    const bDirX = ux * cosR - uz * sinR;
    const bDirZ = ux * sinR + uz * cosR;
    // B starts overlapping A's endpoint by ~rA.
    const bAx = aBx - bDirX * rA;
    const bAz = aBz - bDirZ * rA;
    const bBx = bAx + bDirX * lenB;
    const bBz = bAz + bDirZ * lenB;

    // Capsule C: from B's end toward target, overlapping B by ~rB.
    // lenC is implicit from the tee-to-target distance via B's endpoint.
    const cDx = target.x - bBx;
    const cDz = target.z - bBz;
    const cD = Math.hypot(cDx, cDz) || 1;
    const cDirX = cDx / cD;
    const cDirZ = cDz / cD;
    const cAx = bBx - cDirX * rB;
    const cAz = bBz - cDirZ * rB;
    const cBx = target.x + cDirX * Math.max(3, lenC * 0.05);
    const cBz = target.z + cDirZ * Math.max(3, lenC * 0.05);

    const segments: ArenaCapsule[] = [
      { ax: aAx, az: aAz, bx: aBx, bz: aBz, radius: rA },
      { ax: bAx, az: bAz, bx: bBx, bz: bBz, radius: rB },
      { ax: cAx, az: cAz, bx: cBx, bz: cBz, radius: rC },
    ];

    // Verify tee inside A and target inside C.
    const teeIn =
      distToSegment(tee.x, tee.z, segments[0]!) <= rA - 1;
    const targetIn =
      distToSegment(target.x, target.z, segments[2]!) <= rC - 1;
    if (!teeIn || !targetIn) continue;

    // Bounds circumscribe all three capsules.
    let minX = Infinity,
      maxX = -Infinity,
      minZ = Infinity,
      maxZ = -Infinity;
    for (const s of segments) {
      minX = Math.min(minX, s.ax - s.radius, s.bx - s.radius);
      maxX = Math.max(maxX, s.ax + s.radius, s.bx + s.radius);
      minZ = Math.min(minZ, s.az - s.radius, s.bz - s.radius);
      maxZ = Math.max(maxZ, s.az + s.radius, s.bz + s.radius);
    }
    return { segments, bounds: { minX, maxX, minZ, maxZ } };
  }
  return null;
}

function distToSegment(px: number, pz: number, s: ArenaCapsule): number {
  const cp = closestPointOnSegment2(px, pz, s.ax, s.az, s.bx, s.bz);
  return Math.hypot(px - cp.x, pz - cp.z);
}

function closestPointOnSegment2(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): { x: number; z: number } {
  const abx = bx - ax;
  const abz = bz - az;
  const len2 = abx * abx + abz * abz;
  if (len2 < 1e-12) return { x: ax, z: az };
  let t = ((px - ax) * abx + (pz - az) * abz) / len2;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  return { x: ax + abx * t, z: az + abz * t };
}

function dist(ax: number, az: number, bx: number, bz: number): number {
  return Math.hypot(ax - bx, az - bz);
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
